import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Project360Service } from '../../../modules/p-dashboard/project360.service';
import { DialogWeeklyReportComponent } from '../../../modules/p-dashboard/dashboard/weekly-report-dashboard/dialog-weekly-report/dialog-weekly-report.component';
import { MatDialog } from '@angular/material/dialog';
import { PersonalizationService } from '../../../services/personalization.service';
import { ApexOptions, ChartComponent } from 'ng-apexcharts';
import { TranslocoService } from '@ngneat/transloco';
import { Watchers } from '../../../models/watchers.model';
import { ActivatedRoute, Router } from '@angular/router';
import { WeeklyReportService } from '../../../services/WeeklyReports/weekly-report.service';
import { WeeklyReportDetail } from '../../../models/weeklyReports.model';
import { ToastrService } from 'ngx-toastr';
import { AccessLevel } from '../../../core/user/user.model';
import { UntypedFormGroup } from '@angular/forms';
import { FilesService } from '../../../modules/p-dashboard/files-manager (deprecated)/files.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MatExpansionPanel } from '@angular/material/expansion';
import {
  ConfirmDialogComponent,
  ConfirmDialogModel,
} from '../confirm-dialog/confirm-dialog.component';
import { UserService } from '../../../core/user/user.service';
import { getAreaUnitLabel } from '../../../models/project.model';

@Component({
  selector: 'app-view-weekly-report',
  templateUrl: './view-weekly-report.component.html',
  styleUrls: ['./view-weekly-report.component.scss'],
})
export class ViewWeeklyReportComponent implements OnInit, OnChanges, OnDestroy {
  readonly getAreaUnitLabel = getAreaUnitLabel;
  isNwcClient: boolean = false; // Track if current user is NWC client

  /**
   * ApexCharts draws each chart as a fixed-size SVG without a viewBox, so a print
   * stylesheet cannot scale it down. The charts are re-rendered at a size that fits an
   * A4 portrait column before the print dialog opens, then restored to these sizes.
   */
  private readonly screenBarChartHeight = 300;
  private readonly screenPieChartSize = 350;
  private readonly printChartHeight = 280;
  /** Widths of a printed graph column when the row holds three graphs / two graphs. */
  private readonly printChartWidthInThirds = 200;
  private readonly printChartWidthInHalves = 320;

  constructor(
    public _project: Project360Service,
    public _persona: PersonalizationService,
    public dialog: MatDialog,
    public _route: ActivatedRoute,
    public _toaster: ToastrService,
    private _router: Router,
    private filesService: FilesService,
    private weeklyReportService: WeeklyReportService,
    public translateService: TranslocoService,
    private userService: UserService,
  ) {
    this.barChartOptions = {
      theme: {
        mode: 'dark',
      },
      chart: {
        type: 'bar',
        height: this.screenBarChartHeight,
        background: 'transparent',
        toolbar: {
          show: false,
        },
      },
      colors: [this._persona.colorPrimary1, this._persona.colorAccent.a600],
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
        },
      },
      dataLabels: {
        enabled: true,
        formatter: function (val) {
          return val + '%';
        },
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent'],
      },

      xaxis: {
        categories: [
          this.translateService.translate('Actual'),
          this.translateService.translate('Planned'),
        ],
      },
      title: {
        text: 'Product Trends by Month',
        align: 'left',
      },
      yaxis: {
        max: 100,
        min: 0,
        decimalsInFloat: 0,
        title: {
          text: '% (' + this.translateService.translate('Percentage') + ')',
        },
      },
      fill: {
        opacity: 1,
      },
      tooltip: {
        enabled: false,
      },
    };
  }

  replyAttachments = [];
  public userMentioned: any[] = [];
  @Input() showHeader: boolean = true;
  @Input() reportIdOutsideComponent: string;
  @Input() projectIdOutsideComponent: string;
  @ViewChild('chart') chartElement: ElementRef;
  @ViewChild('barChartRef') barChartRef: ChartComponent;
  @ViewChild('pieChartRef') pieChartRef: ChartComponent;
  @ViewChild(MatExpansionPanel, { static: true })
  weeklyReportExpansionPanel: MatExpansionPanel;

  barChartOptions: ApexOptions;
  pieChartOptions: ApexOptions;
  achivementDisplayedColumns: string[] = ['index', 'description', 'status', 'date'];
  challengeDisplayedColumns: string[] = ['index', 'description', 'action', 'status', 'closingDate'];
  dataSourceAchievements: {
    description: string;
    status: string;
    date: string;
  }[];
  dataSourceChallenges: {
    description: string;
    actionNeeded: string;
    date: string;
    status: string;
  }[];
  mentionableUsersList: Watchers[];
  isLoading: boolean;
  reportId: string;
  isUploadShown: boolean;
  weeklyReportDetails: WeeklyReportDetail;
  weeklyReportComments: any;
  commentId: string;
  timestamp: any;
  totalSpentRemaining: number = 0;
  reportRendering: boolean = true;
  totalImages: number = 0; // Total number of images
  loadedImages: number = 0; // Counter for loaded images

  protected readonly AccessLevel = AccessLevel;

  ngOnChanges(changes: SimpleChanges) {
    if (changes.reportIdOutsideComponent) {
      this.updateReport();
    }
  }

  ngOnInit(): void {
    this.timestamp = new Date().getTime();
    // Check if user is NWC client
    this.isNwcClient = this.userService.isNwcClientSync();
    /* Run this function only when it is used as an internal component */
    if (!this.reportIdOutsideComponent) {
      this.reportId = this._route.snapshot.paramMap.get('report_id') as string;
      this.commentId = this._route.snapshot.paramMap.get('comment_id') as string;
      this.updateReport();
    }
  }

  updateReport() {
    this.timestamp = new Date().getTime();
    this.isLoading = true;
    this.reportRendering = true;
    this.getWeeklyReportDetails();
    this.getWeeklyReportComments();
  }

  /**
   * Generate financial suffix for labels based on client type and input type
   * @returns string - e.g., '%', ' SAR', or empty string
   */
  private getFinancialSuffix(): string {
    if (!this.isNwcClient) {
      return '';
    }
    return this.weeklyReportDetails?.financial_input_type === 'percentage'
      ? '%'
      : ' ' + this.translateService.translate('SAR');
  }

  /**
   * Generate formatted label for chart with value and suffix
   * @param label - The base label (e.g., 'Spent Cost')
   * @param value - The numeric value
   * @returns formatted string like "Spent Cost (100 SAR)"
   */
  private getFormattedFinancialLabel(label: string, value: any): string {
    const translatedLabel = this.translateService.translate(label);
    const suffix = this.getFinancialSuffix();
    return `${translatedLabel}(${value}${suffix})`;
  }

  getWeeklyReportComments() {
    const projectId = this.projectIdOutsideComponent || this._project.id;
    const reportId = this.reportIdOutsideComponent || this.reportId;
    this.weeklyReportService.getWeeklyReportComments(projectId, reportId).subscribe(
      (res) => {
        if (res) {
          this.weeklyReportComments = res.data;
        }
      },
      (error) => {
        this.handleErrors(error);
      },
    );
  }

  getWeeklyReportDetails() {
    const projectId = this.projectIdOutsideComponent || this._project.id;
    const reportId = this.reportIdOutsideComponent || this.reportId;
    this.weeklyReportService.getWeeklyReportDetails(projectId, reportId).subscribe(
      (res) => {
        if (res) {
          this.weeklyReportDetails = res.data;
          this.dataSourceAchievements = res.data.achievements_description;
          this.dataSourceChallenges = res.data.challenges_description;
          this.totalSpentRemaining =
            parseFloat(this.weeklyReportDetails.actual_cost) +
            parseFloat(this.weeklyReportDetails.remaining_cost);
          if (this.weeklyReportDetails.deviation) {
            this.weeklyReportDetails.deviation = this.getDeviatedImageUrl(
              this.weeklyReportDetails.deviation,
            );
          }
          this.setGraphs();
          //counting images to be loaded
          this._persona.logoFull ? this.totalImages++ : null;
          this.weeklyReportDetails.consultant_image ? this.totalImages++ : null;
          this.weeklyReportDetails.contractor_image ? this.totalImages++ : null;
          this.weeklyReportDetails.supervision_image ? this.totalImages++ : null;
          this._project.projectInfo.projectLogo ? this.totalImages++ : null;
          this.weeklyReportDetails.deviation ? this.totalImages++ : null;
        }
        this.isLoading = false;
        // delay for the expansion panel html element to load
        setTimeout(() => {
          this.checkImageLoading();
        }, 300);
      },
      (error) => {
        if (error.status == 404) {
          this._router.navigate(['404']).then();
        }
        this.isLoading = false;
        this.reportRendering = false;
        this.handleErrors(error);
      },
    );
  }

  handleErrors(error: any) {
    error.error.errors.forEach((errorMessage: string) => {
      this._toaster.error(errorMessage);
    });
  }
  openWeeklyReportDialog() {
    const dialogRef = this.dialog.open(DialogWeeklyReportComponent, {
      width: '875px',
      data: {
        projectId: this._project.id,
        totalCost: this._project.projectInfo.totalCost,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this._router.navigate([`project-d/${this._project.id}/dashboard/weekly-report`]);
      }
    });
  }

  setAttachments($event: any) {
    this.replyAttachments = $event;
  }

  addReply($event: UntypedFormGroup): void {
    const projectId = this.projectIdOutsideComponent || this._project.id;
    const reportId = this.reportIdOutsideComponent || this.reportId;
    if ($event) {
      this.isLoading = true;
      const FORM_BODY = new FormData();
      this.replyAttachments.forEach((el) => FORM_BODY.append('attached[]', el));
      this.userMentioned.forEach((user) => FORM_BODY.append('mentions[]', user.uuid));
      Object.keys($event.controls).forEach((control) => {
        FORM_BODY.append(control, $event.controls[control]?.value);
      });

      this.weeklyReportService.createWeeklyReportReply(FORM_BODY, projectId, reportId).subscribe(
        (res) => {
          this.isLoading = false;
          this.replyAttachments = [];
          this.isUploadShown = false;
          this.getWeeklyReportComments();
        },
        (err) => {
          this._toaster.error(err.error.error);
          this.isLoading = false;
        },
      );
    }
  }

  initializeMentionableUsers($event): void {
    $event.pipe(debounceTime(300), distinctUntilChanged()).subscribe((searchValue: string) => {
      if (searchValue && searchValue.length >= 2) {
        this.getMentionableUsers(searchValue);
      }
    });
  }

  getMentionableUsers(searchValue: string): void {
    const projectId = this.projectIdOutsideComponent || this._project.id;
    this.weeklyReportService.getMentionableUsers(projectId, searchValue).subscribe((response) => {
      this.mentionableUsersList = response.data;
    });
  }

  setUserMentioned($event: any) {
    this.userMentioned = $event;
  }

  getCommentPermission(): boolean {
    return this._project.getPermission(AccessLevel.CREATE_WEEKLY_REPORTS_COMMENT);
  }

  downloadFile($event: any) {
    this.filesService.download($event.path, $event.file_name);
  }

  private setGraphs() {
    this.barChartOptions.series = [
      {
        name: this.translateService.translate('Actual'),
        data: [parseFloat(this.weeklyReportDetails?.actual_progress_percentage)],
      },
      {
        name: this.translateService.translate('Planned'),
        data: [parseFloat(this.weeklyReportDetails?.planned_progress_percentage)],
      },
    ];
    this.setPieChart();
  }

  setPieChart() {
    // Calculate the label text and dynamic font size
    const centerLabelText = this.totalSpentRemaining + this.getFinancialSuffix();
    const dynamicFontSize = this.calculateDynamicFontSize(centerLabelText);

    this.pieChartOptions = {
      dataLabels: {
        enabled: false,
      },
      chart: {
        redrawOnParentResize: true,
        redrawOnWindowResize: true,
        fontFamily: 'inherit',
        foreColor: 'inherit',
        height: this.screenPieChartSize,
        width: this.screenPieChartSize,
        type: 'donut',
        sparkline: {
          enabled: false,
        },
        events: {
          dataPointSelection: function (event, chartContext, config) {
            event.preventDefault();
          },
        },
      },
      legend: {
        position: 'bottom',
        offsetY: 0,
      },
      colors: [
        this._persona.colorPrimary1,
        this._persona.colorAccent.a600,
        this._persona.colorAccent.a400,
      ],
      plotOptions: {
        pie: {
          customScale: 1,
          expandOnClick: true,
          donut: {
            size: '77%',
            labels: {
              name: {
                offsetY: 10,
              },
              show: true,
              total: {
                show: true,
                showAlways: true,
                fontSize: dynamicFontSize,
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontWeight: 600,
                label: centerLabelText,
                color: this._persona.colorPrimary1,
              },
              value: {
                offsetY: -5,
                color: this._persona.colorPrimary1,
                show: false,
              },
            },
          },
        },
      },
      series: [
        parseFloat(this.weeklyReportDetails.actual_cost),
        parseFloat(this.weeklyReportDetails.remaining_cost),
        parseFloat(this.weeklyReportDetails.pending_cost),
      ],
      stroke: {
        colors: ['rgba(255,255,255,0.16)'],
      },
      labels: [
        this.getFormattedFinancialLabel('Spent Cost', this.weeklyReportDetails.actual_cost),
        this.getFormattedFinancialLabel('Remaining Cost', this.weeklyReportDetails.remaining_cost),
        this.getFormattedFinancialLabel('Pending Invoices', this.weeklyReportDetails.pending_cost),
      ],
      states: {
        hover: {
          filter: {
            type: 'none',
          },
        },
        active: {
          filter: {
            type: 'none',
          },
        },
      },
      tooltip: {
        enabled: true,
        fillSeriesColor: false,
        theme: 'dark',
        y: {
          formatter: () => '',
          title: {
            formatter: (seriesName) => seriesName,
          },
        },
      },
      title: {
        style: { color: this._persona.colorAccent.a50 },
        text: 'Completion',
        align: 'left',
      },
    };
  }

  getDate(date: string | Date) {
    return new Date(date);
  }

  getDeviatedImageUrl(url: string): string {
    const timestamp = new Date().getTime(); // Unique timestamp for cache bursting
    return `${url}?v=${timestamp}`;
  }

  formatNumber(num) {
    if (num && num >= 1e3) {
      let newNum: any = 0;
      if (num >= 1e9) {
        newNum = (num / 1e9).toFixed(1) + 'B';
      } else if (num >= 1e6) {
        newNum = (num / 1e6).toFixed(1) + 'M';
      } else {
        newNum = (num / 1e3).toFixed(1) + 'K';
      }
      return newNum;
    }
    return num.toString();
  }

  /**
   * Calculate dynamic font size based on text length to ensure it fits in the donut center
   * @param text The text to display in the center
   * @returns Font size in pixels as a string
   */
  calculateDynamicFontSize(text: string): string {
    const textLength = text.length;
    let fontSize = 28; // Start with a larger base size

    // Adjust font size based on text length
    if (textLength <= 6) {
      fontSize = 24;
    } else if (textLength <= 8) {
      fontSize = 20;
    } else if (textLength <= 10) {
      fontSize = 18;
    } else if (textLength <= 12) {
      fontSize = 16;
    } else if (textLength <= 15) {
      fontSize = 14;
    } else {
      fontSize = 12;
    }

    return `${fontSize}px`;
  }

  print(): void {
    this.weeklyReportExpansionPanel.open();

    // Wait for the weekly report panel to expand, then trigger print
    setTimeout(async () => {
      await this.resizeChartsForPrint();
      window.addEventListener('afterprint', this.restoreChartsAfterPrint, { once: true });
      window.print();
    }, 300); // Adjust the delay if necessary to allow the panel to expand
  }

  /**
   * Re-render the graphs at the width of the printed column. Awaiting the update
   * guarantees the smaller SVGs are in the DOM before the print dialog snapshots it.
   */
  private resizeChartsForPrint(): Promise<unknown> {
    const chart = {
      width: this.weeklyReportDetails?.deviation
        ? this.printChartWidthInThirds
        : this.printChartWidthInHalves,
      height: this.printChartHeight,
    };

    return Promise.all([
      this.barChartRef?.updateOptions({ chart }, false, false),
      this.pieChartRef?.updateOptions({ chart }, false, false),
    ]);
  }

  private restoreChartsAfterPrint = (): void => {
    this.barChartRef?.updateOptions(
      { chart: { width: '100%', height: this.screenBarChartHeight } },
      false,
      false,
    );
    this.pieChartRef?.updateOptions(
      { chart: { width: this.screenPieChartSize, height: this.screenPieChartSize } },
      false,
      false,
    );
  };

  ngOnDestroy(): void {
    window.removeEventListener('afterprint', this.restoreChartsAfterPrint);
  }

  checkImageLoading() {
    const imageContainer = document.querySelector('#print-view'); // Replace with your div ID or class
    const images = imageContainer?.querySelectorAll('img') || []; // Select all img elements
    images.forEach((img: HTMLImageElement) => {
      if (img.complete) {
        this.incrementLoadedImages();
      } else {
        img.addEventListener('load', this.incrementLoadedImages.bind(this), {
          once: true,
        });
      }
    });
  }

  incrementLoadedImages() {
    this.loadedImages++;
    if (this.loadedImages === this.totalImages) {
      this.reportRendering = false;
      //check and enable
    }
  }

  getDeletePermisiion() {
    return this._project.getPermission(AccessLevel.DELETE_WEEKLY_REPORTS);
  }

  deleteWeeklyReport(reportId: string) {
    const dialogData = new ConfirmDialogModel(
      this.translateService.translate('Delete Weekly Report'),
      this.translateService.translate('Are you sure you want to delete this report?'),
      true,
      false,
      null,
      this.translateService.translate('Delete'),
    );
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: '640px',
      minWidth: '300px',
      data: dialogData,
    });
    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.weeklyReportService.deleteWeeklyReport(this._project.id, reportId).subscribe(
          async (res) => {
            if (res) {
              this._toaster.success(
                this.translateService.translate('Weekly report deleted successfully'),
              );
              //go back to the table
              this._router.navigate([`project-d/${this._project.id}/dashboard/weekly-report`]);
            }
          },
          (error) => {
            error.error.errors.forEach((errorMessage: string) => {
              this._toaster.error(errorMessage);
            });
          },
        );
      }
    });
  }
}
